/**
 * Viewer.jsx — interactive paper viewer.
 *
 * URL: /viewer/:paperId
 * Layout (left → right):
 *   ┌────────────┬───────────────────────────┬──────────────┐
 *   │ thumbnails │ tabs + scrollable PDF     │ attempts +   │
 *   │            │ + per-page annotations    │ tools        │
 *   └────────────┴───────────────────────────┴──────────────┘
 *
 * Tabs switch the active role file (Question / Markscheme / Model). Annotations
 * are tied to the *current* role's pages (so notes on Q paper stay on Q paper).
 *
 * Login is required to draw/save. Anonymous users get a read-only viewer.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument, rgb } from 'pdf-lib';
import { apiJson, apiUpload, buildApiUrl } from '../api';
import { useAuth } from '../AuthContext';
import { useAttempts } from './useAttempts';
import AnnotationCanvas from './AnnotationCanvas';
import { useSEO } from '../useSEO';

// Worker is copied to /pdfjs/ at build time (see package.json prebuild)
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

const ROLE_TABS = [
  { key: 'question', label: 'Question paper' },
  { key: 'markscheme', label: 'Markscheme' },
  { key: 'model', label: 'Model answer' },
];

const PEN_COLORS = ['#111827', '#dc2626', '#2563eb', '#16a34a', '#a855f7', '#f97316'];
const HIGHLIGHTER_COLORS = ['#fde047', '#86efac', '#fda4af', '#93c5fd'];
const PEN_SIZES = [2, 4, 7, 12];

export default function Viewer() {
  const { paperId } = useParams();
  const { user, token } = useAuth();
  const [paper, setPaper] = useState(null);
  const [error, setError] = useState('');
  useSEO({
    title: paper?.title ? `${paper.title} — Viewer` : 'Paper viewer',
    description: paper?.title
      ? `Interactive paper viewer for ${paper.title}: annotate questions, switch to mark scheme or model answer, and track your attempts.`
      : 'Interactive past-paper viewer with annotations, mark scheme switching and per-attempt timing.',
    canonicalPath: `/viewer/${paperId || ''}`,
  });
  const [activeRole, setActiveRole] = useState('question');
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [zoom, setZoom] = useState(1.25);
  const [tool, setTool] = useState('pen'); // pen | highlighter | eraser
  const [color, setColor] = useState(PEN_COLORS[0]);
  const [size, setSize] = useState(4);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Side-panel collapse state. Default open on desktop, collapsed on tablet/phone
  // so the PDF gets the full width on iPads in portrait.
  const COMPACT_BREAKPOINT = 1100;
  const [viewportW, setViewportW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const compact = viewportW < COMPACT_BREAKPOINT;
  const [leftOpen, setLeftOpen] = useState(viewportW >= COMPACT_BREAKPOINT);
  const [rightOpen, setRightOpen] = useState(viewportW >= COMPACT_BREAKPOINT);

  const attemptsApi = useAttempts({ paperId, token, enabled: !!user });
  const { attempts, activeId, pages, saveStatus, createAttempt, selectAttempt,
    renameAttempt, deleteAttempt, updatePage, flushAll, timerAction, setAttemptScore } = attemptsApi;

  const activeAttempt = useMemo(
    () => attempts.find((a) => a.id === activeId) || null,
    [attempts, activeId]
  );
  const editable = !!user && !!activeAttempt && activeAttempt.status === 'in_progress';

  // ── Load paper metadata ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    apiJson(`/api/papers/${paperId}`)
      .then((data) => {
        if (cancelled) return;
        setPaper(data.paper);
        // Pick first available role
        const firstRole = ROLE_TABS.find((t) => data.paper.files?.[t.key])?.key || 'question';
        setActiveRole(firstRole);
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [paperId]);

  // ── Load PDF whenever active role changes ──────────────────────────────
  useEffect(() => {
    if (!paper) return;
    const file = paper.files?.[activeRole];
    if (!file) { setPdfDoc(null); return; }
    let cancelled = false;
    setPdfLoading(true);
    setPdfDoc(null);
    const url = buildApiUrl(file.url);
    const task = pdfjsLib.getDocument({ url });
    task.promise.then((doc) => {
      if (cancelled) { doc.destroy?.(); return; }
      setPdfDoc(doc);
    }).catch((err) => {
      if (!cancelled) setError(`Failed to load PDF: ${err.message}`);
    }).finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => { cancelled = true; task.destroy?.(); };
  }, [paper, activeRole]);

  // Annotations are namespaced by role so switching tabs preserves separate notes
  const pageKey = useCallback((pageIndex) => `${activeRole}:${pageIndex}`, [activeRole]);

  const onPageChange = useCallback((pageIndex, updater) => {
    if (!editable) return;
    const key = pageKey(pageIndex);
    // Pages stored as one row per pageKey index. We hash role+pageIndex into
    // a stable virtual page index so the backend doesn't need to know roles.
    // Use pageIndex * 10 + role offset to keep them separable yet compact.
    const roleOffset = ROLE_TABS.findIndex((t) => t.key === activeRole);
    const virtualPage = pageIndex * 10 + roleOffset;
    updatePage(virtualPage, updater);
  }, [editable, pageKey, activeRole, updatePage]);

  const getPageStrokes = useCallback((pageIndex) => {
    const roleOffset = ROLE_TABS.findIndex((t) => t.key === activeRole);
    const virtualPage = pageIndex * 10 + roleOffset;
    return pages[virtualPage] || { strokes: [] };
  }, [pages, activeRole]);

  // ── Submit (flatten current attempt's question-paper annotations) ─────
  const handleSubmit = async () => {
    if (!activeAttempt || !pdfDoc) return;
    if (!window.confirm('Submit this attempt? Editing will be locked after submission.')) return;
    setSubmitting(true);
    try {
      await flushAll();
      // Use the QUESTION pdf as the canvas to flatten onto
      const qFile = paper.files?.question;
      if (!qFile) throw new Error('No question paper available to submit');
      const pdfBytes = await fetch(buildApiUrl(qFile.url)).then((r) => r.arrayBuffer());
      const flattened = await flattenAnnotations(pdfBytes, pages, 'question');
      const fd = new FormData();
      fd.append('file', new Blob([flattened], { type: 'application/pdf' }), `${activeAttempt.id}.pdf`);
      await apiUpload(`/api/attempts/${activeAttempt.id}/submit`, fd, { token });
      await attemptsApi.refreshAttempts();
      window.alert('Submitted ✓');
    } catch (err) {
      window.alert('Submit failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Download (flatten current role's annotations and save locally) ────
  const handleDownload = async () => {
    if (!activeAttempt) return;
    const file = paper.files?.[activeRole];
    if (!file) { window.alert('No PDF loaded for this tab'); return; }
    setDownloading(true);
    try {
      await flushAll();
      const pdfBytes = await fetch(buildApiUrl(file.url)).then((r) => r.arrayBuffer());
      const flattened = await flattenAnnotations(pdfBytes, pages, activeRole);
      const blob = new Blob([flattened], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const safe = (s) => String(s || '').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'attempt';
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safe(paper.title)} - ${safe(activeAttempt.name)} (${activeRole}).pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      window.alert('Download failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (error) return <div style={{ padding: 24, color: '#b91c1c' }}>Error: {error}</div>;
  if (!paper) return <div style={{ padding: 24, color: 'var(--text)' }}>Loading paper…</div>;

  const availableRoles = ROLE_TABS.filter((t) => paper.files?.[t.key]);

  return (
    <div style={layout.root}>
      {/* Tabs row */}
      <div style={layout.tabRow}>
        {compact && (
          <button type="button" onClick={() => setLeftOpen((v) => !v)}
            aria-label={leftOpen ? 'Hide page thumbnails' : 'Show page thumbnails'}
            title={leftOpen ? 'Hide pages' : 'Show pages'}
            style={layout.collapseBtn}>
            {leftOpen ? '⟨' : '⟩'} Pages
          </button>
        )}
        <Link to="/" style={layout.backLink}>← Resources</Link>
        <h2 style={layout.title}>{paper.title}</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ROLE_TABS.map((t) => {
            const enabled = !!paper.files?.[t.key];
            const active = t.key === activeRole;
            return (
              <button key={t.key} onClick={() => enabled && setActiveRole(t.key)}
                disabled={!enabled}
                style={{
                  ...layout.tab,
                  ...(active ? layout.tabActive : null),
                  ...(enabled ? null : layout.tabDisabled),
                }}>
                {t.label} {!enabled && '(coming soon)'}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--viewer-muted)' }}>{saveStatusLabel(saveStatus, !!user)}</span>
        {compact && (
          <button type="button" onClick={() => setRightOpen((v) => !v)}
            aria-label={rightOpen ? 'Hide tools' : 'Show tools'}
            title={rightOpen ? 'Hide tools' : 'Show tools'}
            style={layout.collapseBtn}>
            Tools {rightOpen ? '⟩' : '⟨'}
          </button>
        )}
      </div>

      {/* Body */}
      <div style={layout.body}>
        {/* Left rail: always visible on desktop, overlay-toggled on compact */}
        {!compact && <ThumbnailRail pdfDoc={pdfDoc} activeRole={activeRole} />}

        {/* Center PDF */}
        <div style={layout.pdfPane} id={`pdfPane-${activeRole}`}>
          {pdfLoading && <div style={{ padding: 24, color: '#fff' }}>Loading PDF…</div>}
          {pdfDoc && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 16 }}>
              {Array.from({ length: pdfDoc.numPages }, (_, i) => (
                <div key={`${activeRole}-${i}`} id={`pdfPage-${activeRole}-${i + 1}`}>
                  <PdfPage
                    pdfDoc={pdfDoc}
                    pageNumber={i + 1}
                    zoom={zoom}
                    strokes={getPageStrokes(i)}
                    onChange={(updater) => onPageChange(i, updater)}
                    tool={tool}
                    color={color}
                    size={size}
                    disabled={!editable}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right sidebar: always visible on desktop */}
        {!compact && (
          <Sidebar
            user={user}
            tool={tool} setTool={setTool}
            color={color} setColor={setColor}
            size={size} setSize={setSize}
            zoom={zoom} setZoom={setZoom}
            editable={editable}
            activeAttempt={activeAttempt}
            timerAction={timerAction}
            attempts={attempts}
            activeId={activeId}
            selectAttempt={selectAttempt}
            createAttempt={createAttempt}
            renameAttempt={renameAttempt}
            deleteAttempt={deleteAttempt}
            onSubmit={handleSubmit}
            submitting={submitting}
            onDownload={handleDownload}
            downloading={downloading}
            setScore={setAttemptScore}
          />
        )}

        {/* Compact-only overlays */}
        {compact && leftOpen && (
          <>
            <div style={layout.scrim} onClick={() => setLeftOpen(false)} aria-hidden="true" />
            <div style={{ ...layout.overlayPanel, ...layout.overlayLeft }}>
              <ThumbnailRail pdfDoc={pdfDoc} activeRole={activeRole} />
            </div>
          </>
        )}

        {compact && rightOpen && (
          <>
            <div style={layout.scrim} onClick={() => setRightOpen(false)} aria-hidden="true" />
            <div style={{ ...layout.overlayPanel, ...layout.overlayRight }}>
              <Sidebar
                user={user}
                tool={tool} setTool={setTool}
                color={color} setColor={setColor}
                size={size} setSize={setSize}
                zoom={zoom} setZoom={setZoom}
                editable={editable}
                activeAttempt={activeAttempt}
                timerAction={timerAction}
                attempts={attempts}
                activeId={activeId}
                selectAttempt={selectAttempt}
                createAttempt={createAttempt}
                renameAttempt={renameAttempt}
                deleteAttempt={deleteAttempt}
                onSubmit={handleSubmit}
                submitting={submitting}
                onDownload={handleDownload}
                downloading={downloading}
                setScore={setAttemptScore}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Sidebar({ user, tool, setTool, color, setColor, size, setSize, zoom, setZoom,
  editable, activeAttempt, timerAction, attempts, activeId, selectAttempt, createAttempt,
  renameAttempt, deleteAttempt, onSubmit, submitting, onDownload, downloading, setScore }) {
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <aside style={layout.sidebar}>
      {!user && (
        <div style={layout.guestNotice}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>✍️ Write your attempt on this PDF</div>
          <div style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text)' }}>
            With a free account you can draw and annotate directly on the paper, time yourself, and save your attempt to come back to later.
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <Link to={`/register?next=${next}`} className="primary-btn" style={{ textDecoration: 'none', padding: '4px 10px', fontSize: 13 }}>Create free account</Link>
            <Link to={`/login?next=${next}`} className="secondary-btn" style={{ textDecoration: 'none', padding: '4px 10px', fontSize: 13 }}>Log in</Link>
          </div>
        </div>
      )}
      <ToolPanel
        tool={tool} setTool={setTool}
        color={color} setColor={setColor}
        size={size} setSize={setSize}
        zoom={zoom} setZoom={setZoom}
        disabled={!editable}
      />
      {user && (
        <TimerPanel
          attempt={activeAttempt}
          onAction={(action) => activeAttempt && timerAction(activeAttempt.id, action)}
        />
      )}
      {user && (
        <AttemptsPanel
          attempts={attempts}
          activeId={activeId}
          onSelect={selectAttempt}
          onCreate={() => createAttempt()}
          onRename={renameAttempt}
          onDelete={deleteAttempt}
          activeAttempt={activeAttempt}
          onSubmit={onSubmit}
          submitting={submitting}
          onDownload={onDownload}
          downloading={downloading}
          onSetScore={setScore}
        />
      )}
    </aside>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════

function PdfPage({ pdfDoc, pageNumber, zoom, strokes, onChange, tool, color, size, disabled }) {
  const canvasRef = useRef(null);
  const [dims, setDims] = useState(null); // { pdfW, pdfH, displayW, displayH }
  const [renderError, setRenderError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;

    pdfDoc.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      const baseViewport = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: zoom * (window.devicePixelRatio || 1) });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${baseViewport.width * zoom}px`;
      canvas.style.height = `${baseViewport.height * zoom}px`;
      renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      renderTask.promise.then(() => {
        if (cancelled) return;
        setDims({
          pdfW: baseViewport.width, pdfH: baseViewport.height,
          displayW: baseViewport.width * zoom, displayH: baseViewport.height * zoom,
        });
      }).catch((err) => {
        if (err?.name !== 'RenderingCancelledException' && !cancelled) {
          setRenderError(err.message || 'render failed');
        }
      });
    });

    return () => {
      cancelled = true;
      try { renderTask?.cancel(); } catch (_) { /* ignore */ }
    };
  }, [pdfDoc, pageNumber, zoom]);

  return (
    <div style={{ position: 'relative', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
      <canvas ref={canvasRef} style={{ display: 'block', background: '#fff' }} />
      {dims && (
        <AnnotationCanvas
          pageWidth={dims.pdfW}
          pageHeight={dims.pdfH}
          displayScale={zoom}
          strokes={strokes}
          onChange={onChange}
          tool={tool}
          color={color}
          size={size}
          disabled={disabled}
        />
      )}
      {renderError && <div style={{ position: 'absolute', top: 4, left: 4, color: '#b91c1c', fontSize: 12 }}>{renderError}</div>}
    </div>
  );
}

function ThumbnailRail({ pdfDoc, activeRole }) {
  const [thumbs, setThumbs] = useState([]);
  useEffect(() => {
    if (!pdfDoc) { setThumbs([]); return; }
    let cancelled = false;
    (async () => {
      const out = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        if (cancelled) return;
        try {
          const page = await pdfDoc.getPage(i);
          const viewport = page.getViewport({ scale: 0.2 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          out.push({ idx: i, dataUrl: canvas.toDataURL('image/png'), ratio: viewport.width / viewport.height });
          if (!cancelled) setThumbs([...out]);
        } catch (_) { /* ignore single-page failures */ }
      }
    })();
    return () => { cancelled = true; };
  }, [pdfDoc]);

  const jumpTo = (idx) => {
    const el = document.getElementById(`pdfPage-${activeRole}-${idx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={layout.thumbs}>
      {thumbs.map((t) => (
        <button key={t.idx} type="button" onClick={() => jumpTo(t.idx)}
          style={{ ...layout.thumbBox, aspectRatio: String(t.ratio || (1 / 1.414)), padding: 0, background: '#fff', cursor: 'pointer', textAlign: 'left', flexShrink: 0 }}>
          <img src={t.dataUrl} alt={`Page ${t.idx}`} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} />
          <div style={layout.thumbLabel}>{t.idx}</div>
        </button>
      ))}
    </div>
  );
}

function ToolPanel({ tool, setTool, color, setColor, size, setSize, zoom, setZoom, disabled }) {
  const palette = tool === 'highlighter' ? HIGHLIGHTER_COLORS : PEN_COLORS;
  const showColor = tool !== 'eraser' && tool !== 'select';
  return (
    <div style={layout.panel}>
      <h3 style={layout.panelTitle}>Tools</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
        <ToolBtn label="Pen" active={tool === 'pen'} onClick={() => setTool('pen')} disabled={disabled} />
        <ToolBtn label="Highlight" active={tool === 'highlighter'} onClick={() => { setTool('highlighter'); setColor(HIGHLIGHTER_COLORS[0]); }} disabled={disabled} />
        <ToolBtn label="Erase" active={tool === 'eraser'} onClick={() => setTool('eraser')} disabled={disabled} />
        <ToolBtn label="Text" active={tool === 'text'} onClick={() => { setTool('text'); if (HIGHLIGHTER_COLORS.includes(color)) setColor(PEN_COLORS[0]); }} disabled={disabled} />
        <ToolBtn label="Rect" active={tool === 'rect'} onClick={() => { setTool('rect'); if (HIGHLIGHTER_COLORS.includes(color)) setColor(PEN_COLORS[0]); }} disabled={disabled} />
        <ToolBtn label="Line" active={tool === 'line'} onClick={() => { setTool('line'); if (HIGHLIGHTER_COLORS.includes(color)) setColor(PEN_COLORS[0]); }} disabled={disabled} />
        <ToolBtn label="Select" active={tool === 'select'} onClick={() => setTool('select')} disabled={disabled} />
      </div>

      {showColor && (
        <>
          <div style={{ fontSize: 12, color: 'var(--viewer-muted)', marginBottom: 4 }}>Colour</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {palette.map((c) => (
              <button key={c} onClick={() => setColor(c)} disabled={disabled}
                style={{ width: 22, height: 22, borderRadius: '50%', border: color === c ? '2px solid var(--text)' : '1px solid var(--viewer-border)', background: c, cursor: 'pointer' }} />
            ))}
          </div>
        </>
      )}

      {tool !== 'eraser' && tool !== 'select' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--viewer-muted)', marginBottom: 4 }}>
            {tool === 'text' ? 'Text size' : 'Thickness'}
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {PEN_SIZES.map((s) => (
              <button key={s} onClick={() => setSize(s)} disabled={disabled}
                style={{
                  width: 28, height: 28, borderRadius: 6,
                  border: size === s ? '2px solid var(--text)' : '1px solid var(--viewer-border)',
                  background: 'var(--viewer-input-bg)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <span style={{ width: tool === 'text' ? Math.min(s + 4, 16) : s, height: tool === 'text' ? Math.min(s + 4, 16) : s, background: 'var(--text)', borderRadius: tool === 'text' ? 2 : '50%', fontSize: 8 }} />
              </button>
            ))}
          </div>
        </>
      )}

      {tool === 'select' && (
        <div style={{ fontSize: 12, color: 'var(--viewer-muted)', marginBottom: 10 }}>
          Drag a region to lasso items, or click to pick one. Drag selection to move; press Backspace to delete.
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--viewer-muted)', marginBottom: 4 }}>Zoom · {(zoom * 100).toFixed(0)}%</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))} style={layout.smallBtn}>−</button>
        <button onClick={() => setZoom(1)} style={layout.smallBtn}>1×</button>
        <button onClick={() => setZoom((z) => Math.min(3, z + 0.1))} style={layout.smallBtn}>+</button>
      </div>
    </div>
  );
}

function ToolBtn({ label, active, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
      border: active ? '2px solid var(--link)' : '1px solid var(--viewer-border)',
      background: active ? 'rgba(37,99,235,0.18)' : 'var(--viewer-input-bg)',
      color: 'var(--text)',
      opacity: disabled ? 0.5 : 1,
    }}>{label}</button>
  );
}

function TimerPanel({ attempt, onAction }) {
  const [now, setNow] = useState(Date.now());
  const running = !!attempt?.timerStartedAt;
  // Tick every second only while running, to keep the display live.
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  if (!attempt) {
    return (
      <div style={layout.panel}>
        <h3 style={layout.panelTitle}>Timer</h3>
        <div style={{ fontSize: 12, color: 'var(--viewer-muted)' }}>Select an attempt to time it.</div>
      </div>
    );
  }

  const banked = Number(attempt.timerSeconds || 0);
  const liveExtra = running ? Math.max(0, Math.floor((now - new Date(attempt.timerStartedAt).getTime()) / 1000)) : 0;
  const total = banked + liveExtra;
  const locked = attempt.status !== 'in_progress';

  return (
    <div style={layout.panel}>
      <h3 style={layout.panelTitle}>Timer</h3>
      <div style={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 28, fontWeight: 600, textAlign: 'center', marginBottom: 8,
        color: running ? '#22c55e' : 'var(--text)',
      }}>{formatHMS(total)}</div>
      {locked ? (
        <div style={{ fontSize: 12, color: 'var(--viewer-muted)', textAlign: 'center' }}>Locked — submitted.</div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          {!running ? (
            <button onClick={() => onAction('start')}
              style={{ ...layout.smallBtn, flex: 1, background: '#16a34a', color: '#fff', borderColor: '#16a34a' }}>
              {banked > 0 ? 'Resume' : 'Start'}
            </button>
          ) : (
            <button onClick={() => onAction('pause')}
              style={{ ...layout.smallBtn, flex: 1, background: '#f59e0b', color: '#fff', borderColor: '#f59e0b' }}>
              Pause
            </button>
          )}
          <button onClick={() => { if (window.confirm('Reset timer to zero?')) onAction('reset'); }}
            disabled={banked === 0 && !running}
            style={{ ...layout.smallBtn, opacity: (banked === 0 && !running) ? 0.5 : 1 }}>Reset</button>
        </div>
      )}
    </div>
  );
}

function formatHMS(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
}

function AttemptsPanel({ attempts, activeId, onSelect, onCreate, onRename, onDelete, activeAttempt, onSubmit, submitting, onDownload, downloading, onSetScore }) {
  const [editing, setEditing] = useState(false);
  const [scoreInput, setScoreInput] = useState('');
  const [totalInput, setTotalInput] = useState('');
  useEffect(() => {
    if (!activeAttempt) { setEditing(false); return; }
    setScoreInput(activeAttempt.score == null ? '' : String(activeAttempt.score));
    setTotalInput(activeAttempt.totalMarks == null ? '' : String(activeAttempt.totalMarks));
  }, [activeAttempt?.id, activeAttempt?.score, activeAttempt?.totalMarks]);

  const saveScore = async () => {
    if (!activeAttempt || !onSetScore) return;
    try {
      await onSetScore(activeAttempt.id, {
        score: scoreInput === '' ? null : Number(scoreInput),
        totalMarks: totalInput === '' ? null : Number(totalInput),
      });
      setEditing(false);
    } catch (err) {
      window.alert('Failed to save score: ' + err.message);
    }
  };

  return (
    <div style={layout.panel}>
      <h3 style={layout.panelTitle}>Attempts</h3>
      <button onClick={onCreate} style={{ ...layout.smallBtn, width: '100%', marginBottom: 8, background: '#2563eb', color: '#fff', borderColor: '#2563eb' }}>
        + New attempt
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12, maxHeight: 220, overflowY: 'auto' }}>
        {attempts.map((a) => (
          <div key={a.id} style={{
            padding: 6, borderRadius: 6, fontSize: 13, cursor: 'pointer',
            border: a.id === activeId ? '2px solid var(--accent, #2563eb)' : '1px solid var(--viewer-border, #e2e8f0)',
            background: a.id === activeId ? 'rgba(37,99,235,0.12)' : 'var(--viewer-surface, #fff)',
            color: 'var(--text)',
          }} onClick={() => onSelect(a.id)}>
            <div style={{ fontWeight: 600 }}>{a.name}</div>
            <div style={{ fontSize: 11, color: 'var(--viewer-muted, #64748b)' }}>
              {a.status}{a.score !== null ? ` · ${a.score}/${a.totalMarks ?? '?'}` : ''}
            </div>
            {a.id === activeId && (
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <button style={layout.linkBtn} onClick={(e) => { e.stopPropagation(); const n = prompt('Rename', a.name); if (n) onRename(a.id, n); }}>rename</button>
                <button style={layout.linkBtn} onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this attempt?')) onDelete(a.id); }}>delete</button>
              </div>
            )}
          </div>
        ))}
        {!attempts.length && <div style={{ color: 'var(--viewer-muted, #94a3b8)', fontSize: 12 }}>No attempts yet — create one to start drawing.</div>}
      </div>

      {activeAttempt && activeAttempt.status === 'in_progress' && (
        <button onClick={onSubmit} disabled={submitting}
          style={{ ...layout.smallBtn, width: '100%', background: '#16a34a', color: '#fff', borderColor: '#16a34a' }}>
          {submitting ? 'Submitting…' : 'Submit attempt'}
        </button>
      )}
      {activeAttempt && onDownload && (
        <button onClick={onDownload} disabled={downloading}
          title="Download a PDF of the current tab with your annotations baked in"
          style={{ ...layout.smallBtn, width: '100%', marginTop: 6, background: '#2563eb', color: '#fff', borderColor: '#2563eb' }}>
          {downloading ? 'Preparing…' : '⬇ Download annotated PDF'}
        </button>
      )}
      {activeAttempt && activeAttempt.status !== 'in_progress' && (
        <div style={{ fontSize: 12, color: 'var(--viewer-muted, #64748b)' }}>
          Submitted. Editing is locked. (AI marking coming soon.)
        </div>
      )}

      {activeAttempt && onSetScore && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--viewer-border-soft)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--viewer-muted)' }}>Score</span>
            {!editing && (
              <button type="button" style={layout.linkBtn} onClick={() => setEditing(true)}>
                {activeAttempt.score == null ? 'add' : 'edit'}
              </button>
            )}
          </div>
          {!editing && (
            <div style={{ fontSize: 14 }}>
              {activeAttempt.score == null
                ? <span style={{ color: 'var(--viewer-muted)' }}>Not recorded</span>
                : <strong>{activeAttempt.score}{activeAttempt.totalMarks ? ` / ${activeAttempt.totalMarks}` : ''}{activeAttempt.totalMarks ? ` · ${Math.round((activeAttempt.score / activeAttempt.totalMarks) * 100)}%` : ''}</strong>}
            </div>
          )}
          {editing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="number" min="0" step="any" value={scoreInput}
                  onChange={(e) => setScoreInput(e.target.value)}
                  placeholder="score"
                  style={{ width: '50%', padding: '4px 6px', borderRadius: 4, border: '1px solid var(--viewer-border)', background: 'var(--viewer-input-bg)', color: 'var(--text)' }} />
                <span style={{ color: 'var(--viewer-muted)' }}>/</span>
                <input type="number" min="1" step="1" value={totalInput}
                  onChange={(e) => setTotalInput(e.target.value)}
                  placeholder="total"
                  style={{ width: '50%', padding: '4px 6px', borderRadius: 4, border: '1px solid var(--viewer-border)', background: 'var(--viewer-input-bg)', color: 'var(--text)' }} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={saveScore} style={{ ...layout.smallBtn, flex: 1, background: '#2563eb', color: '#fff', borderColor: '#2563eb' }}>Save</button>
                <button type="button" onClick={() => setEditing(false)} style={{ ...layout.smallBtn, flex: 1 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════════════

function saveStatusLabel(s, loggedIn) {
  if (!loggedIn) return 'read-only mode';
  return ({ idle: '', dirty: 'unsaved…', saving: 'saving…', saved: 'saved ✓', error: 'save failed' })[s] || '';
}

/**
 * Flatten annotations onto the question PDF and return the resulting PDF bytes.
 * Only the QUESTION role is flattened (that's what the AI marker needs).
 *
 * Pages map to the question role using the same encoding as `onPageChange`:
 *   virtualPage = pageIndex * 10 + 0  (role offset for question = 0)
 */
async function flattenAnnotations(pdfBytes, pages, role) {
  const { getStroke } = await import('perfect-freehand');
  const doc = await PDFDocument.load(pdfBytes);
  const numPages = doc.getPageCount();
  const ROLE_OFFSET = { question: 0, markscheme: 1, model: 2 };
  const offset = ROLE_OFFSET[role] ?? 0;

  for (let i = 0; i < numPages; i++) {
    const virtualPage = i * 10 + offset;
    const pageData = pages[virtualPage];
    if (!pageData?.strokes?.length) continue;
    const page = doc.getPage(i);
    const { width: pdfW, height: pdfH } = page.getSize();

    for (const s of pageData.strokes) {
      const col = hexToRgb(s.color || '#111');
      const opacity = s.tool === 'highlighter' ? 0.35 : 1;
      // Y-flip helper: pdf-lib uses bottom-left origin, our coords use top-left
      const flipY = (y) => pdfH - y;

      if (s.tool === 'highlighter' || s.tool === 'pen') {
        if (s.tool === 'highlighter') {
          for (let k = 1; k < s.points.length; k++) {
            const [x1, y1] = s.points[k - 1];
            const [x2, y2] = s.points[k];
            page.drawLine({
              start: { x: x1, y: flipY(y1) }, end: { x: x2, y: flipY(y2) },
              thickness: s.size, color: rgb(col.r, col.g, col.b), opacity,
            });
          }
        } else {
          const outline = getStroke(s.points, {
            size: s.size, thinning: 0.5, smoothing: 0.5, streamline: 0.4,
            simulatePressure: !s.points[0]?.[2],
          });
          for (let k = 1; k < outline.length; k++) {
            const [x1, y1] = outline[k - 1];
            const [x2, y2] = outline[k];
            page.drawLine({
              start: { x: x1, y: flipY(y1) }, end: { x: x2, y: flipY(y2) },
              thickness: 1, color: rgb(col.r, col.g, col.b), opacity,
            });
          }
        }
      } else if (s.tool === 'rect') {
        const x = Math.min(s.x, s.x + s.w);
        const y = Math.min(s.y, s.y + s.h);
        const w = Math.abs(s.w), h = Math.abs(s.h);
        page.drawRectangle({
          x, y: flipY(y + h), width: w, height: h,
          borderColor: rgb(col.r, col.g, col.b), borderWidth: s.size, opacity,
        });
      } else if (s.tool === 'line') {
        page.drawLine({
          start: { x: s.x1, y: flipY(s.y1) }, end: { x: s.x2, y: flipY(s.y2) },
          thickness: s.size, color: rgb(col.r, col.g, col.b), opacity,
        });
      } else if (s.tool === 'text') {
        const fs = s.fontSize || 16;
        page.drawText(s.text || '', {
          x: s.x + 4, y: flipY(s.y + fs * 0.85),
          size: fs, color: rgb(col.r, col.g, col.b),
        });
      }
    }
  }
  return doc.save();
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(m[1], 16) / 255, g: parseInt(m[2], 16) / 255, b: parseInt(m[3], 16) / 255 };
}

// ════════════════════════════════════════════════════════════════════════
// Inline styles
// ════════════════════════════════════════════════════════════════════════

const layout = {
  root: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', background: 'var(--viewer-bg)', color: 'var(--text)' },
  tabRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: '1px solid var(--viewer-border)', background: 'var(--viewer-tab-bg)', flexWrap: 'wrap' },
  collapseBtn: { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--viewer-border)', background: 'var(--viewer-surface)', color: 'var(--text)', cursor: 'pointer', fontSize: 12, fontWeight: 600, touchAction: 'manipulation', minHeight: 36 },
  backLink: { fontSize: 13, color: 'var(--link)', textDecoration: 'none' },
  title: { margin: 0, fontSize: 16, fontWeight: 600, marginRight: 16 },
  tab: { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--viewer-border)', background: 'var(--viewer-surface)', color: 'var(--text)', cursor: 'pointer', fontSize: 13 },
  tabActive: { background: 'var(--link)', color: '#fff', borderColor: 'var(--link)' },
  tabDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  body: { flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' },
  scrim: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 20 },
  overlayPanel: { position: 'absolute', top: 0, bottom: 0, zIndex: 21, boxShadow: '0 4px 24px rgba(0,0,0,0.2)' },
  overlayLeft: { left: 0 },
  overlayRight: { right: 0 },
  thumbs: { width: 'min(160px, 70vw)', flexShrink: 0, height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: 8, background: 'var(--viewer-surface)', borderRight: '1px solid var(--viewer-border)', display: 'flex', flexDirection: 'column', gap: 8, boxSizing: 'border-box' },
  thumbBox: { position: 'relative', border: '1px solid var(--viewer-border)', borderRadius: 4, overflow: 'hidden' },
  thumbLabel: { position: 'absolute', bottom: 2, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, padding: '0 4px', borderRadius: 2 },
  pdfPane: { flex: 1, overflow: 'auto', background: 'var(--viewer-pdf-bg)' },
  sidebar: { width: 'min(300px, 85vw)', padding: 12, background: 'var(--viewer-surface)', borderLeft: '1px solid var(--viewer-border)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--text)' },
  panel: { border: '1px solid var(--viewer-border-soft)', borderRadius: 8, padding: 10, background: 'var(--viewer-panel)' },
  panelTitle: { margin: '0 0 8px', fontSize: 14 },
  smallBtn: { padding: '4px 10px', borderRadius: 6, border: '1px solid var(--viewer-border)', background: 'var(--viewer-input-bg)', color: 'var(--text)', cursor: 'pointer', fontSize: 13 },
  linkBtn: { background: 'transparent', border: 'none', color: 'var(--link)', cursor: 'pointer', fontSize: 11, padding: 0, textDecoration: 'underline' },
  notice: { background: 'rgba(252,211,77,0.18)', border: '1px solid rgba(252,211,77,0.5)', borderRadius: 6, padding: 8, fontSize: 13, color: 'var(--text)' },
  guestNotice: { background: 'rgba(99, 102, 241, 0.12)', border: '1px solid rgba(99, 102, 241, 0.45)', borderRadius: 8, padding: '10px 12px' },
};
